/* eslint-disable react-hooks/exhaustive-deps */
import DateRangePill from 'components/DateRangePill';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import WrapTextIcon from '@mui/icons-material/WrapText';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Button from 'components/Button';
import { HeaderPage } from 'components/HeaderPage/HeaderPage';
import { SearchTracking } from 'components/SearchTracking';
import { API_ROUTES } from 'data/ApiRoutes';
import instance from 'data/services/api';
import { getAllBranchCompaniesOverview } from 'data/api/company.api';
import { CompanyBranch } from 'data/models/company.model';
import { CompanyInfo } from 'data/models/company.model';
import { selectCompany } from 'store/companySlide';
import { buildApiPath } from 'utils';
import { handleTabs, TabEnum } from '../company-detail/CompanDetailNew';
import ExportTraking from 'assets/icon-components/exportTracking';
import { DASHBOARD_ROUTES } from 'Routes';

interface MessageSentRow {
  location: string;
  patientName: string | null;
  deathDate: string | null;
  memberFirstName: string;
  memberLastName: string;
  userId: string;
  sentOn: string;
  body: string;
  contentId: string | null;
  conversationPartId: string;
  eventTypeId: string | null;
  eventType: string | null;
}

interface EventTypeOption {
  eventTypeId: string;
  abbreviatedName: string;
}

const EVENT_TYPE_LABEL_ID = 'outreach-event-type-label';
const RETRY_EVENT_TYPES = '__retry__';

const PAGE_SIZE_OPTIONS = [15, 25, 50];

const headerSx = {
  fontWeight: 700,
  fontFamily: 'Plus Jakarta Sans',
  fontSize: '13px',
  lineHeight: '20px',
  letterSpacing: '0.1px',
  color: '#7D818C',
  cursor: 'pointer',
  userSelect: 'none' as const,
  whiteSpace: 'nowrap' as const,
};

const cellSx = {
  fontFamily: 'Plus Jakarta Sans',
  fontSize: '13px',
  color: '#213B54',
  verticalAlign: 'top',
  paddingY: '12px',
};

const filterLabelSx = {
  position: 'absolute',
  top: -9,
  left: 10,
  fontSize: '11px',
  fontWeight: 700,
  color: '#213B54',
  backgroundColor: 'white',
  px: 0.5,
  lineHeight: 1,
  zIndex: 1,
  pointerEvents: 'none',
} as const;

const filterSelectSx = {
  width: '100%',
  height: '40px',
  borderRadius: '10px',
  '& .MuiSelect-select': { fontSize: '13px' },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#213B54',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#213B54',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#213B54',
  },
};

const statusItemSx = {
  fontSize: '13px',
  color: '#7D818C',
  '&.Mui-disabled': { opacity: 1 },
};

type SortField =
  | 'location'
  | 'patient_name'
  | 'death_date'
  | 'member_name'
  | 'sent_on'
  | 'body';

interface SortState {
  field: SortField;
  order: 'asc' | 'desc';
}

const SortIcon: React.FC<{ field: SortField; sort: SortState }> = ({
  field,
  sort,
}) => {
  if (sort.field !== field) {
    return (
      <UnfoldMoreIcon
        sx={{
          fontSize: '16px',
          ml: 0.5,
          verticalAlign: 'middle',
          opacity: 0.4,
        }}
      />
    );
  }
  return sort.order === 'asc' ? (
    <ExpandLessIcon
      sx={{ fontSize: '16px', ml: 0.5, verticalAlign: 'middle' }}
    />
  ) : (
    <ExpandMoreIcon
      sx={{ fontSize: '16px', ml: 0.5, verticalAlign: 'middle' }}
    />
  );
};

export const CompanyMessagesSentPage: React.FC = () => {
  const { company_id } = useParams();
  const companyInfo: CompanyInfo = useSelector(selectCompany);
  const [searchParams, setSearchParams] = useSearchParams();
  const getParam = (key: string) => searchParams.get(key) ?? undefined;

  const page = Number(getParam('page') ?? 0);
  const pageSize = Number(getParam('pageSize') ?? 25);
  const sortField = (getParam('sortBy') ?? 'sent_on') as SortField;
  const sortOrder = (getParam('order') ?? 'desc') as 'asc' | 'desc';

  const sort: SortState = { field: sortField, order: sortOrder };

  const [rows, setRows] = useState<MessageSentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [allBranchCompany, setAllBranchCompany] = useState<CompanyBranch[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  const [wrapMessages, setWrapMessages] = useState(false);
  const [eventTypes, setEventTypes] = useState<EventTypeOption[]>([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(true);
  const [eventTypesError, setEventTypesError] = useState(false);
  const [eventTypesReloadKey, setEventTypesReloadKey] = useState(0);
  // Every label seen this visit, so a selection that drops out of the options
  // still reads as what the user picked.
  const [eventTypeLabels, setEventTypeLabels] = useState<
    Record<string, string>
  >({});

  const selectedBranchIds = (getParam('branchIds') ?? '')
    .split(',')
    .filter(Boolean);
  const selectedEventTypeIds = (getParam('eventTypeIds') ?? '')
    .split(',')
    .filter(Boolean);
  const isFiltered =
    !!getParam('query') ||
    (allBranchCompany.length > 0 &&
      selectedBranchIds.length < allBranchCompany.length) ||
    !!getParam('sentOnStart') ||
    !!getParam('sentOnEnd') ||
    !!getParam('deathDateStart') ||
    !!getParam('deathDateEnd') ||
    selectedEventTypeIds.length > 0;

  // The options route takes the list's filters minus pagination and
  // eventTypeIds, so picking a type never narrows the menu down to itself.
  const filterParams = {
    query: getParam('query') || undefined,
    branchIds: getParam('branchIds') || undefined,
    sentOnStart: getParam('sentOnStart') || undefined,
    sentOnEnd: getParam('sentOnEnd') || undefined,
    deathDateStart: getParam('deathDateStart') || undefined,
    deathDateEnd: getParam('deathDateEnd') || undefined,
  };
  const listFilterParams = {
    ...filterParams,
    eventTypeIds: getParam('eventTypeIds') || undefined,
  };

  const fetchData = useCallback(async () => {
    if (!company_id) return;
    setLoading(true);
    try {
      const res = await instance.get(
        buildApiPath({
          path: API_ROUTES.companiesMessagesSent(company_id),
          params: {
            page,
            pageSize,
            ...listFilterParams,
            sortBy: sortField,
            order: sortOrder,
          },
        }),
      );
      setRows(res.data?.items ?? []);
      setTotal(res.data?.total ?? 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [searchParams, company_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const eventTypesPath = company_id
    ? buildApiPath({
        path: API_ROUTES.companiesMessagesSentEventTypes(company_id),
        params: filterParams,
      })
    : undefined;

  useEffect(() => {
    if (!eventTypesPath) return;
    let cancelled = false;
    setEventTypesLoading(true);
    setEventTypesError(false);
    instance
      .get(eventTypesPath)
      .then((res) => {
        if (cancelled) return;
        const list: EventTypeOption[] = res.data?.eventTypes ?? [];
        setEventTypes(list);
        setEventTypeLabels((prev) =>
          list.reduce(
            (acc, t) => ({ ...acc, [t.eventTypeId]: t.abbreviatedName }),
            prev,
          ),
        );
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setEventTypesError(true);
      })
      .finally(() => {
        if (!cancelled) setEventTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventTypesPath, eventTypesReloadKey]);

  useEffect(() => {
    if (!companyInfo?.isParentCompany || !company_id) return;
    getAllBranchCompaniesOverview(company_id)
      .then((companies) => {
        const list = companies ?? [];
        setAllBranchCompany(list);
        if (!searchParams.get('branchIds') && list.length) {
          const params = new URLSearchParams(searchParams);
          params.set('branchIds', list.map((b) => b.companyId).join(','));
          setSearchParams(params, { replace: true });
        }
      })
      .catch(console.error);
  }, [companyInfo?.isParentCompany, company_id]);

  const eventTypeLabel = (id: string) =>
    eventTypeLabels[id] ?? 'Unavailable type';
  const availableEventTypes =
    eventTypesLoading || eventTypesError ? [] : eventTypes;
  // Selected types missing from the current options stay listed (checked) so
  // the user can see and remove them.
  const eventTypeMenuOptions: EventTypeOption[] = [
    ...availableEventTypes,
    ...selectedEventTypeIds
      .filter((id) => !availableEventTypes.some((t) => t.eventTypeId === id))
      .map((id) => ({ eventTypeId: id, abbreviatedName: eventTypeLabel(id) })),
  ];
  // No eventTypeIds is All, shown the way Branch Location shows it: every
  // type checked. The URL keeps All as "no filter" rather than a list because
  // the options change with the other filters.
  const allEventTypesSelected = selectedEventTypeIds.length === 0;
  const checkedEventTypeIds = allEventTypesSelected
    ? availableEventTypes.map((t) => t.eventTypeId)
    : selectedEventTypeIds;

  const updateParams = (updates: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v !== undefined && v !== '') {
        params.set(k, v);
      } else {
        params.delete(k);
      }
    });
    params.set('page', '0');
    setSearchParams(params);
  };

  const handleSort = (field: SortField) => {
    if (sort.field === field) {
      updateParams({
        sortBy: field,
        order: sort.order === 'asc' ? 'desc' : 'asc',
      });
    } else {
      updateParams({ sortBy: field, order: 'asc' });
    }
  };

  const resetFilters = () => {
    const params = new URLSearchParams();
    params.set('page', '0');
    params.set('pageSize', String(pageSize));
    params.set('sortBy', 'sent_on');
    params.set('order', 'desc');
    if (allBranchCompany.length) {
      params.set(
        'branchIds',
        allBranchCompany.map((b) => b.companyId).join(','),
      );
    }
    setSearchParams(params);
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handlePageSizeChange = (newSize: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('pageSize', String(newSize));
    params.set('page', '0');
    setSearchParams(params);
  };

  const exportCsv = async () => {
    if (!company_id) return;
    setExportLoading(true);
    try {
      const allRows: MessageSentRow[] = [];
      let p = 0;
      const size = 500;
      let keepFetching = true;
      while (keepFetching) {
        const res = await instance.get(
          buildApiPath({
            path: API_ROUTES.companiesMessagesSent(company_id),
            params: {
              page: p,
              pageSize: size,
              ...listFilterParams,
              sortBy: sortField,
              order: sortOrder,
            },
          }),
        );
        const chunk: MessageSentRow[] = res.data?.items ?? [];
        allRows.push(...chunk);
        if (allRows.length >= (res.data?.total ?? 0) || chunk.length < size) {
          keepFetching = false;
        } else {
          p++;
        }
      }

      const headers = [
        'Branch Location',
        'Event Type',
        'Patient Name',
        'Death Date',
        'Member Name',
        'Sent On',
        'Message',
      ];
      const escapeCsv = (val: string) => `"${(val ?? '').replace(/"/g, '""')}"`;
      const csvRows = [
        headers.join(','),
        ...allRows.map((r) => {
          const deathDate = r.deathDate
            ? moment(r.deathDate).format('M/D/YYYY')
            : '';
          const sentOn = r.sentOn
            ? moment(r.sentOn).format('M/D/YYYY h:mm A')
            : '';
          const memberName = `${r.memberFirstName} ${r.memberLastName}`;
          return [
            escapeCsv(r.location ?? ''),
            escapeCsv(r.eventType ?? ''),
            escapeCsv(r.patientName ?? ''),
            escapeCsv(deathDate),
            escapeCsv(memberName),
            escapeCsv(sentOn),
            escapeCsv(r.body ?? ''),
          ].join(',');
        }),
      ];

      const blob = new Blob([csvRows.join('\n')], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${companyInfo?.name ?? 'company'}_outreach_report.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setExportLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const rangeStart = total === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = Math.min((page + 1) * pageSize, total);

  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      pages.push(0);
      if (page > 2) pages.push('...');
      for (
        let i = Math.max(1, page - 1);
        i <= Math.min(totalPages - 2, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 3) pages.push('...');
      pages.push(totalPages - 1);
    }
    return pages;
  };

  const filterDropdownSx = {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#213B54 !important',
      borderRadius: '10px',
    },
    '& .MuiInputLabel-root': {
      fontWeight: 700,
      fontSize: '14px',
    },
  };

  return (
    <>
      <HeaderPage
        title={{
          title: companyInfo?.name ? companyInfo.name : '',
          hasBack: true,
        }}
        tabs={handleTabs(
          company_id || '',
          companyInfo?.isParentCompany || false,
          TabEnum.OutreachReport,
        )}
      />

      <Stack
        marginTop={'16px'}
        marginLeft={'140px'}
        marginRight={{ xs: '16px', lg: '80px' }}
      >
        {/* Toolbar */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: '20px', color: '#213B54' }}
          >
            Outreach Report
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#213B54',
              color: '#ffffff',
              fontWeight: 700,
              height: '40px',
            }}
            startIcon={
              exportLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ExportTraking />
              )
            }
            onClick={exportCsv}
            disabled={exportLoading}
          >
            Export.csv
          </Button>
        </Stack>

        {/* Filters */}
        <Stack
          direction={{ xs: 'column', xl: 'row' }}
          spacing={1.2}
          flexWrap="wrap"
          alignItems={{ xs: 'stretch', xl: 'center' }}
          sx={{ mb: 2 }}
          gap={1.2}
        >
          <SearchTracking
            onChange={(query: string) =>
              updateParams({ query: query || undefined })
            }
            debounce={300}
            label="Search for a member or patient"
          />

          {companyInfo?.isParentCompany && (
            <Box
              sx={{
                position: 'relative',
                minWidth: 225,
                width: { xs: '100%', xl: '225px' },
              }}
            >
              <Typography sx={filterLabelSx}>Branch Location</Typography>
              <Select
                size="small"
                multiple
                displayEmpty
                value={(getParam('branchIds') ?? '').split(',').filter(Boolean)}
                onChange={(e) => {
                  const val = e.target.value as string[];
                  if (val.includes('__all__')) {
                    updateParams({
                      branchIds:
                        selectedBranchIds.length === allBranchCompany.length
                          ? undefined
                          : allBranchCompany
                              .map((b) => b.companyId ?? '')
                              .join(','),
                    });
                  } else {
                    const filtered = val.filter(Boolean);
                    updateParams({
                      branchIds: filtered.length
                        ? filtered.join(',')
                        : undefined,
                    });
                  }
                }}
                renderValue={(selected) => {
                  const sel = selected as string[];
                  if (!sel.length) return 'All';
                  const names = allBranchCompany
                    .filter((b) => b.companyId && sel.includes(b.companyId))
                    .map((b) => b.name);
                  return (
                    <Tooltip title={names.join(', ')} placement="bottom-start">
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {names.join(', ')}
                      </span>
                    </Tooltip>
                  );
                }}
                sx={filterSelectSx}
              >
                <MenuItem value="__all__" sx={{ pl: 1 }}>
                  <Checkbox
                    checked={
                      selectedBranchIds.length === allBranchCompany.length
                    }
                    size="small"
                  />
                  <ListItemText
                    primary="All"
                    primaryTypographyProps={{ fontSize: '13px' }}
                  />
                </MenuItem>
                {allBranchCompany.map((branch) => (
                  <MenuItem
                    key={branch.companyId}
                    value={branch.companyId}
                    sx={{ pl: 1 }}
                  >
                    <Checkbox
                      checked={(getParam('branchIds') ?? '')
                        .split(',')
                        .includes(branch.companyId ?? '')}
                      size="small"
                    />
                    <ListItemText
                      primary={branch.name}
                      primaryTypographyProps={{ fontSize: '13px' }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          <Box
            sx={{
              position: 'relative',
              minWidth: 225,
              width: { xs: '100%', xl: '225px' },
            }}
          >
            <Typography id={EVENT_TYPE_LABEL_ID} sx={filterLabelSx}>
              Event Type
            </Typography>
            <Select
              size="small"
              multiple
              displayEmpty
              labelId={EVENT_TYPE_LABEL_ID}
              value={checkedEventTypeIds}
              onChange={(e) => {
                const val = e.target.value as string[];
                if (val.includes(RETRY_EVENT_TYPES)) {
                  setEventTypesReloadKey((k) => k + 1);
                  return;
                }
                if (val.includes('__all__')) {
                  updateParams({ eventTypeIds: undefined });
                  return;
                }
                const filtered = val.filter(Boolean);
                // Against the whole menu, so dropping one retained type
                // keeps the others instead of resetting to All.
                const everyTypeChecked =
                  eventTypeMenuOptions.length > 0 &&
                  eventTypeMenuOptions.every((t) =>
                    filtered.includes(t.eventTypeId),
                  );
                updateParams({
                  eventTypeIds:
                    filtered.length && !everyTypeChecked
                      ? filtered.join(',')
                      : undefined,
                });
              }}
              renderValue={(selected) => {
                const sel = selected as string[];
                if (allEventTypesSelected || !sel.length) return 'All';
                const label = sel.map(eventTypeLabel).join(', ');
                return (
                  <Tooltip title={label} placement="bottom-start">
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </span>
                  </Tooltip>
                );
              }}
              sx={filterSelectSx}
            >
              <MenuItem value="__all__" sx={{ pl: 1 }}>
                <Checkbox checked={allEventTypesSelected} size="small" />
                <ListItemText
                  primary="All"
                  primaryTypographyProps={{ fontSize: '13px' }}
                />
              </MenuItem>
              {eventTypesLoading ? (
                <MenuItem disabled sx={statusItemSx}>
                  <CircularProgress
                    size={14}
                    sx={{ mr: 1.25, color: '#7D818C' }}
                  />
                  Loading event types…
                </MenuItem>
              ) : eventTypesError ? (
                <MenuItem
                  value={RETRY_EVENT_TYPES}
                  sx={{ ...statusItemSx, color: '#213B54' }}
                >
                  Couldn’t load event types.&nbsp;
                  <span
                    style={{ color: '#E07249', textDecoration: 'underline' }}
                  >
                    Retry
                  </span>
                </MenuItem>
              ) : eventTypes.length === 0 ? (
                <MenuItem disabled sx={statusItemSx}>
                  No event types in this range
                </MenuItem>
              ) : null}
              {eventTypeMenuOptions.map((t) => (
                <MenuItem
                  key={t.eventTypeId}
                  value={t.eventTypeId}
                  sx={{ pl: 1 }}
                >
                  <Checkbox
                    checked={checkedEventTypeIds.includes(t.eventTypeId)}
                    size="small"
                  />
                  <ListItemText
                    primary={t.abbreviatedName}
                    primaryTypographyProps={{ fontSize: '13px' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </Box>

          <DateRangePill
            label="Sent On"
            startValue={getParam('sentOnStart') ?? ''}
            endValue={getParam('sentOnEnd') ?? ''}
            onStartChange={(val) =>
              updateParams({ sentOnStart: val || undefined })
            }
            onEndChange={(val) => updateParams({ sentOnEnd: val || undefined })}
          />

          <DateRangePill
            label="Death Date"
            startValue={getParam('deathDateStart') ?? ''}
            endValue={getParam('deathDateEnd') ?? ''}
            onStartChange={(val) =>
              updateParams({ deathDateStart: val || undefined })
            }
            onEndChange={(val) =>
              updateParams({ deathDateEnd: val || undefined })
            }
          />

          <Button
            startIcon={<ClearIcon />}
            onClick={resetFilters}
            disabled={!isFiltered}
            sx={{ fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            Reset Filters
          </Button>
        </Stack>

        {/* Pagination row above table */}
        {!loading && total > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
              Showing {rangeStart}–{rangeEnd} of {total}
            </Typography>

            <Stack direction="row" alignItems="center" gap={1}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
                  Rows per page:
                </Typography>
                <Select
                  size="small"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  sx={{
                    fontSize: '13px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '8px',
                    },
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>

              <Stack direction="row" alignItems="center" gap={0.5}>
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => handlePageChange(page - 1)}
                  sx={{ borderRadius: '8px' }}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                {getPageNumbers().map((p, idx) =>
                  p === '...' ? (
                    <Typography
                      key={`ellipsis-${idx}`}
                      sx={{ px: 1, color: '#7D818C', fontSize: '13px' }}
                    >
                      …
                    </Typography>
                  ) : (
                    <IconButton
                      key={p}
                      size="small"
                      onClick={() => handlePageChange(p as number)}
                      sx={{
                        borderRadius: '8px',
                        minWidth: 32,
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        fontWeight: p === page ? 700 : 400,
                        backgroundColor: p === page ? '#213B54' : 'transparent',
                        color: p === page ? '#fff' : '#213B54',
                        '&:hover': {
                          backgroundColor:
                            p === page ? '#213B54' : 'rgba(33,59,84,0.08)',
                        },
                      }}
                    >
                      {(p as number) + 1}
                    </IconButton>
                  ),
                )}
                <IconButton
                  size="small"
                  disabled={page >= totalPages - 1}
                  onClick={() => handlePageChange(page + 1)}
                  sx={{ borderRadius: '8px' }}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        )}

        {/* Table */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    borderBottom: '2px solid #E0E0E0',
                    backgroundColor: '#FAFAFA',
                  },
                }}
              >
                <TableCell sx={headerSx} onClick={() => handleSort('location')}>
                  Branch Location
                  <SortIcon field="location" sort={sort} />
                </TableCell>
                <TableCell sx={{ ...headerSx, cursor: 'default' }}>
                  Event Type
                </TableCell>
                <TableCell
                  sx={headerSx}
                  onClick={() => handleSort('patient_name')}
                >
                  Patient Name
                  <SortIcon field="patient_name" sort={sort} />
                </TableCell>
                <TableCell
                  sx={headerSx}
                  onClick={() => handleSort('death_date')}
                >
                  Death Date
                  <SortIcon field="death_date" sort={sort} />
                </TableCell>
                <TableCell
                  sx={headerSx}
                  onClick={() => handleSort('member_name')}
                >
                  Member Name
                  <SortIcon field="member_name" sort={sort} />
                </TableCell>
                <TableCell sx={headerSx} onClick={() => handleSort('sent_on')}>
                  Sent On
                  <SortIcon field="sent_on" sort={sort} />
                </TableCell>
                <TableCell sx={{ ...headerSx, width: '35%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Message
                    <Tooltip
                      title={
                        wrapMessages ? 'Collapse to single line' : 'Wrap text'
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => setWrapMessages((prev) => !prev)}
                        sx={{
                          p: 0.25,
                          color: wrapMessages ? '#213B54' : '#BDBDBD',
                          '&:hover': { color: '#213B54' },
                        }}
                      >
                        <WrapTextIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: pageSize }).map((_, i) => (
                  <TableRow key={i}>
                    {[1, 2, 3, 4, 5, 6, 7].map((c) => (
                      <TableCell key={c}>
                        <Skeleton variant="text" width="80%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ color: '#7D818C', fontSize: '14px' }}>
                      {isFiltered ? (
                        <>
                          No messages match your filters.{' '}
                          <span
                            onClick={resetFilters}
                            style={{
                              color: '#E07249',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            Reset filters
                          </span>
                        </>
                      ) : (
                        'No proactive messages have been sent yet'
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const rowId = row.conversationPartId;
                  const isExpanded = expandedRows.has(rowId);
                  return (
                    <TableRow
                      key={rowId}
                      sx={{
                        '&:hover': { backgroundColor: '#FAF7F4' },
                        '& td': { borderBottom: '1px solid #F0F0F0' },
                      }}
                    >
                      <TableCell sx={cellSx}>{row.location ?? '—'}</TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                        {row.eventType ?? '—'}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {row.patientName ?? '—'}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                        {row.deathDate
                          ? moment(row.deathDate).format('M/D/YYYY')
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                        <a
                          href={DASHBOARD_ROUTES.USER_DETAIL_ID(row.userId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: '#1565C0',
                            textDecoration: 'underline',
                            fontWeight: 500,
                          }}
                        >
                          {row.memberFirstName} {row.memberLastName}
                        </a>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                        {row.sentOn
                          ? moment(row.sentOn).format('M/D/YYYY h:mm A')
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, width: '35%' }}>
                        {wrapMessages ? (
                          <Typography
                            sx={{
                              fontSize: '13px',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {row.body}
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 0.5,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {isExpanded ? (
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {row.body}
                                </Typography>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '400px',
                                  }}
                                >
                                  {row.body}
                                </Typography>
                              )}
                            </Box>
                            {row.body && row.body.length > 60 && (
                              <Tooltip
                                title={isExpanded ? 'Collapse' : 'Expand'}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRow(rowId)}
                                  sx={{ flexShrink: 0, mt: '-4px' }}
                                >
                                  {isExpanded ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </>
  );
};
